import styled, { css } from 'styled-components';
import Markdown from 'react-markdown';
import { always, cond, equals, T } from 'ramda';

import { media } from '../../utils/media';
import { TRANSPARENT, SCORE_PALETTE, WHITE } from '../../utils/colors';
import { ContentContainer } from '../layout/layout.styles';
import { propCond } from '../../utils/propCond';
import { TABLE_IDS } from './table.constants';

export const Title = styled(Markdown)`
  margin-bottom: 24px;

  h3,
  p {
    font-size: 18px;
    font-weight: 600;
    line-height: 1.11em;
    letter-spacing: 0.18px;
    text-transform: uppercase;
    margin-bottom: 0;
  }
`;

export const Score = styled.div`
  border-radius: 50%;
  color: ${WHITE};
  background-color: ${({ index }) => SCORE_PALETTE[index]};
  width: 46px;
  line-height: 46px;
  height: 46px;
  margin: 0 auto;
  text-align: center;
  font-weight: 600;
  font-size: 12px;
`;

export const PillarIcon = styled.div`
  display: inline-block;
  position: absolute;

  svg {
    width: 45px;
    height: 45px;
    fill: ${({ theme }) => theme.color};
    border-radius: 50%;
    border: 1px solid ${({ theme }) => theme.color};
  }
`;

export const PillarTitle = styled.div`
  display: inline-block;
  margin-left: 55px;
  margin-top: 9px;
  line-height: 1.17em;
`;

const cellStyles = css`
  padding: 8px 14px;
  line-height: 1.5em;
  letter-spacing: 0.3px;

  ${media.tablet`
    padding: 12px 20px;
  `}
`;

export const HeaderCell = styled.th`
  ${cellStyles}
  font-size: 14px;
  font-weight: 600;
  line-height: 1.43em;
  letter-spacing: 0.35px;
  text-transform: uppercase;
`;

export const Cell = styled.td`
  ${cellStyles}
  font-size: 12px;
  position: relative;
  border-bottom: none;
  border-top: 1px solid ${({ theme }) => theme.color};
  vertical-align: top;
    ${cond([
      [({ hasValue, rowContinue }) => hasValue && !rowContinue, ({ theme }) => theme.color],
      [({ useColor, rowContinue, pillar }) => useColor && rowContinue && !pillar, ({ theme }) => theme.background],
      [T, always(TRANSPARENT)],
    ])};

  ${({ pillar }) =>
    pillar &&
    css`
      padding-left: 0;
    `}
`;

export const MarkdownContent = styled(Markdown)`
p {
  margin: 0;
}
  li {
    margin: 0;
  }
`

export const HiddenCell = styled.td`
  display: none;
`;

export const Row = styled.tr`
  ${Cell} {
    background-color: ${({ useColor, theme }) => (useColor ? theme.background : TRANSPARENT)};

    ${({ firstColumnHighlighted, hasMergedRows, hasPreservedBackground }) =>
      firstColumnHighlighted &&
      css`
        :first-child {
          text-transform: uppercase;
          font-weight: 500;
          color: ${({ theme }) => theme.color};
          ${hasMergedRows &&
          !hasPreservedBackground &&
          css`
            background-color: ${TRANSPARENT};
          `}
        }
      `}
  }
`;

const tableIdCond = propCond('tableId');

export const TableComponent = styled.table`
  margin-bottom: 0;
  table-layout: fixed;

  ${tableIdCond([
    [
      equals(TABLE_IDS.PILLAR),
      always(css`
          ${Cell} {
            vertical-align: top;
          }

          ${Cell}:nth-child(n + 2), ${HeaderCell} {
            padding: 0 20px;
            line-height: 20px;
          }
        `),
    ],
    [
      equals(TABLE_IDS.SCORE),
      always(css`
        ${HeaderCell}:first-child {
          width: 120px;
          text-align: center;
        }
      `),
    ],
    [
      equals(TABLE_IDS.STAKEHOLDER_INVOLVEMENT),
      always(css`
        ${Cell}, ${HeaderCell} {
          font-size: 11px;
          vertical-align: top;

          ${media.tablet`
            font-size: 12px;
          `}
        }
      `),
    ],
    [
      equals(TABLE_IDS.CRISIS_BUDGETING_PILLAR),
      always(css`
        td:first-child {
          background: white;
        }
        td:nth-child(2) {
          font-family: 'IBM Plex Sans';
          padding: 21px 20px 0;
          font-style: normal;
          font-weight: 600;
          font-size: 12px;
          line-height: 16px;
          text-transform: uppercase;
          color: ${({ theme }) => theme.color};
        }
        td:last-child {
          padding: 21px 20px;
        }
      `)
    ]
  ])}
`;

export const TableHeader = styled.thead`
  ${tableIdCond([
    [
      equals(TABLE_IDS.PILLAR),
      always(css`
        ${HeaderCell}:first-child {
          padding-left: 55px;
          width: 210px;
        }
      `),
    ],
  ])}
`;

export const TableBody = styled.tbody`
  border-bottom: 1px solid ${({ theme }) => theme.color};
  td:first-child {
    font-family: 'IBM Plex Sans';
    font-style: normal;
    font-weight: 600;
    font-size: 12px;
    line-height: 16px;
    text-transform: uppercase;
    color: ${({ theme }) => theme.color};
  }
`;

export const Footnote = styled(Markdown)`
  margin-top: 10px;

  p {
    font-size: 12px;
    line-height: 1.5em;
    letter-spacing: 0.3px;
    margin-bottom: 0;
  }
`;

const colorsStyles = ({ theme }) => css`
  ${HeaderCell} {
    border-bottom: 2px solid ${theme.color};
  }

  ${Title}, ${HeaderCell}, ${Footnote} {
    color: ${theme.color};
  }
`;

export const Container = styled(ContentContainer)`
  display: block;
  margin: 32px 37px 64px 32px;
  ${colorsStyles}

  padding-left: 0;

  ${media.tablet`
    margin: 32px auto 64px auto;
  `}
`;
